import { reactive } from 'vue';
import { signInHistoryToday } from './signInHistoryCalendar.js';

const datePattern = /^\d{4}-\d{2}-\d{2}$/;
export function validSignDate(value) {
  if (typeof value !== 'string' || !datePattern.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
const money = value => value !== '' && value != null && Number.isFinite(Number(value)) && Number(value) >= 0 ? String(value) : '—';
// 盲盒范围来自服务端预览，不能当作本次已抽到的金额。
export function signInBlindBoxPreview(value) {
  if (value == null) return null;
  const { minimumAmount, maximumAmount } = value;
  if (![minimumAmount, maximumAmount].every(amount => typeof amount === 'string' && /^\d+\.\d{2}$/.test(amount) && Number.isFinite(Number(amount)) && Number(amount) > 0) || Number(minimumAmount) > Number(maximumAmount)) throw new Error('盲盒奖励范围不完整，请重新查询');
  return { minimumAmount, maximumAmount };
}
// 分笔沿服务端原凭证展示，不用总额乘某一个倍数重算流水。
export function signInRewardPartsLabel(parts) {
  if (parts == null) return '';
  if (!Array.isArray(parts) || parts.length < 2 || parts.length > 121 || parts.some(part => typeof part?.title !== 'string' || !part.title.trim() || ![part.amount, part.requiredTurnover].every(value => typeof value === 'string' && /^\d+\.\d{2}$/.test(value) && Number.isFinite(Number(value))) || Number(part.amount) <= 0 || !/^\d+(?:\.\d{1,2})?$/.test(part.turnoverMultiplier) || !Number.isFinite(Number(part.turnoverMultiplier)))) throw new Error('签到奖励分笔不完整，请重新查询');
  return parts.map(part => `${part.title} ${part.amount} 元，${part.turnoverMultiplier} 倍流水（${part.requiredTurnover} 元）`).join('；');
}
export function signInRepairRewardLabel(day) {
  const preview = signInBlindBoxPreview(day.blindBox);
  const parts = signInRewardPartsLabel(day.rewardParts);
  if (parts && (preview || !(day.recordId > 0))) throw new Error('补签奖励分笔状态不一致，请重新查询');
  return preview ? `盲盒 ${preview.minimumAmount}～${preview.maximumAmount} 元` : `${day.rewardAmount} 元${parts ? `；${parts}` : ''}`;
}
const paid = record => Number(record.status) === 2 && Number(record.rewardAmount) > 0;
const deferredSignState = record => ({ PENDING_CLAIM: 1, PENDING_REVIEW: 1, PENDING_DISPATCH: 1, REJECTED: 3 }[record.signRewardState] === Number(record.status) && Number(record.frozenSignAmount) > 0);
const signedReceipt = (record, legacy) => paid(record) || legacy && (deferredSignState(record) || record.zeroRewardSign === true && Number(record.status) === 2 && Number(record.rewardAmount) === 0);
// 状态和明细分开读取，领取提交期间可能跨到不同快照；只读核对，绝不重发SIGN或CLAIM。
function manualRecordMismatch(status, history) {
  if (status?.manualClaim !== true || status.signedToday !== true || typeof status.rewardClaimed !== 'boolean' || !Array.isArray(history?.data)) return false;
  const record = history.data.find(row => Number(row.id) === Number(status.pendingRecordId) && row.signDate === status.today);
  return !record || (status.rewardClaimed ? !paid(record) : Number(record.status) !== 1);
}
// A16 由模板标识或服务端有效分支契约识别；普通签到的零流水不是模板标识。
export function isTemplateSignIn(status, activity = {}) {
  return activity.templateCode === 'A16' || activity.activityType === 'ACTIVITY_TEMPLATE_A16' || Boolean(status?.manualClaim === true && Array.isArray(status.signModes) && status.signModes.length && status.signModes.some(mode => [1, 2].includes(mode?.category) && mode.category === status.signCategory));
}

export const isSignInActivityType = type => ['SIGN_IN', 'WEEK_SIGN_IN'].includes(type);

export async function resolveSignInActivity(api, requestedId = '') {
  let id = requestedId;
  if (id !== '' && !/^[1-9]\d*$/.test(String(id))) throw new Error('签到活动编号无效');
  if (!id) {
    const rows = await api.list();
    if (!Array.isArray(rows)) throw new Error('签到活动查询失败，请重试');
    id = rows.find(item => item.activityType === 'SIGN_IN')?.id;
    if (!id) return null;
  }
  if (!/^[1-9]\d*$/.test(String(id))) throw new Error('签到活动编号无效');
  const detail = await api.detail(id);
  if (!detail?.id || String(detail.id) !== String(id) || !isSignInActivityType(detail.activityType)) throw new Error('该活动不是可用的签到活动');
  return detail;
}

// Only the existing single-cycle contract is mapped here. Demo milestone IDs
// must never become real rewardId/action parameters.
export function signInModel(activity, status, records = []) {
  if (!status || !validSignDate(status.today) || typeof status.signedToday !== 'boolean') throw new Error('签到状态不完整，请重新查询');
  const manualClaim = status.manualClaim === true;
  const venueBonusIds = status.venueBonusIds ?? [];
  if (!Array.isArray(venueBonusIds) || venueBonusIds.some(id => !Number.isSafeInteger(id) || id <= 0) || new Set(venueBonusIds).size !== venueBonusIds.length || venueBonusIds.length && !manualClaim) throw new Error('签到场馆原单状态不完整，请重新查询');
  const pendingRepairs = new Map();
  for (const day of manualClaim && Array.isArray(status.repairDays) ? status.repairDays : []) {
    if (!(day?.recordId > 0)) continue;
    if (!Number.isSafeInteger(day.recordId) || !validSignDate(day.date) || day.date >= status.today || !/^\d+\.\d{2}$/.test(day.rewardAmount) || !(Number(day.rewardAmount) > 0) || day.blindBox || pendingRepairs.has(day.date)) throw new Error('补签原凭证状态不完整，请重新查询');
    signInRepairRewardLabel(day);
    pendingRepairs.set(day.date, day);
  }
  const rewardPartsLabel = signInRewardPartsLabel(status.rewardParts);
  if (rewardPartsLabel && (!manualClaim || !status.signedToday || status.blindBox)) throw new Error('签到奖励分笔状态不一致，请重新查询');
  const signModes = status.signModes == null ? [] : status.signModes;
  const signCategory = status.signCategory || 0;
  if (!Array.isArray(signModes) || signModes.some(mode => ![1, 2].includes(mode?.category) || typeof mode.title !== 'string' || !mode.title.trim()) || new Set(signModes.map(mode => mode.category)).size !== signModes.length || signModes.length && (!manualClaim || !signModes.some(mode => mode.category === signCategory)) || signCategory && !signModes.length || status.signBlockedReason != null && typeof status.signBlockedReason !== 'string') throw new Error('签到分支状态不完整，请重新查询');
  const signBlockedReason = manualClaim && typeof status.signBlockedReason === 'string' ? status.signBlockedReason : '';
  const blindBox = signInBlindBoxPreview(status.blindBox);
  if (blindBox && (!manualClaim || status.signedToday || status.rewardClaimed)) throw new Error('盲盒签到状态不一致，请重新查询');
  const week = activity.activityType === 'WEEK_SIGN_IN' ? status.week : null;
  const cycleDays = week?.days?.length;
  const fixedCycle = week?.cycleType === 1;
  if (activity.activityType === 'WEEK_SIGN_IN' && (!week || !validSignDate(week.startDate) || !validSignDate(week.endDate) || typeof week.canSign !== 'boolean' || !Array.isArray(week.days) || (fixedCycle ? cycleDays < 5 || cycleDays > 30 : cycleDays !== 7) || !week.days.every(day => validSignDate(day.date) && typeof day.signed === 'boolean' && money(day.rewardAmount) !== '—') || money(week.requiredBet) === '—' || Number(week.requiredBet) <= 0 || money(week.validBetAmount) === '—' || money(week.fullReward) === '—' || !Number.isSafeInteger(week.signedDays) || week.signedDays < 0 || week.signedDays > cycleDays)) throw new Error('本周签到资格不完整，请重新查询');
  if (week) {
    if (week.cycleType != null && ![1, 2].includes(week.cycleType) || week.cumulative != null && typeof week.cumulative !== 'boolean') throw new Error('签到周期类型不完整，请重新查询');
    const monday = new Date(`${week.startDate}T00:00:00Z`);
    const expected = index => new Date(monday.getTime() + index * 86400000).toISOString().slice(0, 10);
    if (manualClaim || (!fixedCycle && monday.getUTCDay() !== 1) || week.endDate !== expected(cycleDays - 1) || !week.days.every((day, index) => day.date === expected(index) && (!day.signed || day.date <= status.today)) || week.days.filter(day => day.signed).length !== week.signedDays || Boolean(week.days.find(day => day.date === status.today)?.signed) !== status.signedToday || week.canSign && (status.signedToday || Number(week.validBetAmount) < Number(week.requiredBet))) throw new Error('本周签到资格不一致，请重新查询');
  }
  const awardWay = week ? (status.awardWay == null ? 1 : status.awardWay) : 0;
  const pendingIds = status.pendingRecordIds == null && awardWay !== 3 ? [] : status.pendingRecordIds;
  const pendingAmount = status.pendingAmount == null && awardWay !== 3 ? '0.00' : money(status.pendingAmount);
  const autoRepairCount = status.autoRepairCount ?? 0;
  if (week && (![1, 2, 3].includes(awardWay) || !Array.isArray(pendingIds) || pendingIds.some(id => !Number.isSafeInteger(id) || id <= 0) || new Set(pendingIds).size !== pendingIds.length || pendingAmount === '—' || Boolean(pendingIds.length) !== (Number(pendingAmount) > 0) || !Number.isSafeInteger(autoRepairCount) || autoRepairCount < 0 || autoRepairCount > (status.repairRemaining || 0) || status.autoRepair != null && typeof status.autoRepair !== 'boolean' || (status.reviewPendingCount != null && (!Number.isSafeInteger(status.reviewPendingCount) || status.reviewPendingCount < 0)))) throw new Error('签到派奖状态不完整，请重新查询');
  const pendingRecordId = Number(status.pendingRecordId);
  if (manualClaim && (typeof status.rewardClaimed !== 'boolean' || (status.signedToday && (!Number.isSafeInteger(pendingRecordId) || pendingRecordId <= 0)))) throw new Error('签到领奖状态不完整，请重新查询');
  const today = new Date(`${status.today}T00:00:00Z`);
  const dates = new Set(records.filter(paid).map(record => record.signDate));
  let [startDate, endDate] = [activity.startAt, activity.endAt].map(value => {
    const timestamp = Number(value) * 1000;
    return Number.isFinite(timestamp) && timestamp > 0 && timestamp <= 8640000000000000 - 8 * 3600000 ? signInHistoryToday(timestamp) : '';
  });
  if (manualClaim) {
    // 指定日期签到可以短于活动展示期，未配置的期外日期不展示未来大奖。
    const selectedTitle = signModes.find(mode => mode.category === signCategory)?.title;
    const sections = (Array.isArray(activity.ruleSections) ? activity.ruleSections : []).filter(section => !selectedTitle || section.title === selectedTitle || section.title === `${selectedTitle}时间`);
    const configuredDates = sections.flatMap(section => Array.isArray(section?.items) ? section.items : [])
      .map(item => item?.title).filter(validSignDate).sort();
    if (configuredDates.length) {
      const configuredStart = configuredDates[0], configuredEnd = configuredDates[configuredDates.length - 1];
      startDate = startDate && startDate > configuredStart ? startDate : configuredStart;
      endDate = endDate && endDate < configuredEnd ? endDate : configuredEnd;
    }
  }
  return {
    templateSignIn: isTemplateSignIn(status, activity),
    hero: activity.mainImageUrl ? { src: activity.mainImageUrl, alt: activity.title || '签到活动' } : null,
    today: status.today,
    signCategory,
    modes: signModes.length > 1 ? signModes.map(mode => ({ id: mode.category, label: mode.title })) : undefined,
    modeId: signModes.length > 1 ? signCategory : undefined,
    activityStartDate: startDate,
    repairLimit: Number.isSafeInteger(status.repairLimit) && status.repairLimit > 0 ? status.repairLimit : 0,
    repairPending: Array.isArray(status.repairDays) && status.repairDays.some(day => Number.isSafeInteger(day.recordId) && day.recordId > 0),
    signedToday: status.signedToday,
    manualClaim,
    venueBonusIds: [...venueBonusIds],
    blindBox,
    pendingRecordId: manualClaim ? pendingRecordId : 0,
    canClaim: manualClaim && status.signedToday && !status.rewardClaimed,
    rewardClaimed: manualClaim || week && awardWay !== 1 ? status.rewardClaimed : status.signedToday,
    canSign: !status.signedToday && !signBlockedReason && (!week || week.canSign),
    week: Boolean(week),
    blockedReason: signBlockedReason || (typeof week?.blockedReason === 'string' ? week.blockedReason : ''),
    awardWay,
    autoRepair: Boolean(week && status.autoRepair),
    autoRepairCount,
    pendingRecordIds: [...(pendingIds || [])],
    pendingAmount,
    canClaimAll: Boolean(week && pendingIds.length && Number(pendingAmount) > 0),
    reviewPendingCount: week ? status.reviewPendingCount || 0 : 0,
    dailyRewardState: week?.days.find(day => day.date === status.today)?.rewardState || '',

    cycleEnded: Boolean(week && status.today > week.endDate),
    cycleNotStarted: Boolean(week && status.today < week.startDate),
    eligibility: week ? { current: money(week.validBetAmount), required: money(week.requiredBet), canParticipate: week.canSign || status.signedToday, actionLabel: '查看参与条件' } : undefined,
    qualificationRule: week ? `${week.blockedReason ? `${week.blockedReason}。` : ''}${Number.isInteger(week.level) && week.level > 0 ? `今日已达第${week.level}档。` : ''}按北京时间当天已结算人民币有效投注统计；期外、作废及未结算投注不计入。` : '',
    streakDays: Number.isInteger(status.streakDays) && status.streakDays >= 0 ? status.streakDays : '—',
    dailyReward: blindBox ? '盲盒' : money(status.dailyReward), nextReward: blindBox ? '待开启' : money(status.nextReward),
    days: week ? week.days.map((day, index) => ({ date: day.date, label: fixedCycle ? day.date.slice(5).replace('-', '.') : ['周一', '周二', '周三', '周四', '周五', '周六', '周日'][index], current: day.date === status.today,
      reward: money(day.rewardAmount), rewardState: day.rewardState || '', state: day.signed ? 'signed' : startDate && day.date < startDate ? 'not-started' : endDate && day.date > endDate ? 'ended' : day.date === status.today ? 'today' : day.date > status.today ? 'future' : 'expired' })) : Array.from({ length: 6 }, (_, index) => {
      const date = new Date(today);
      date.setUTCDate(date.getUTCDate() + index - 4);
      const key = date.toISOString().slice(0, 10), current = index === 4;
      const record = records.find(row => row.signDate === key && paid(row));
      const pendingRepair = pendingRepairs.get(key);
      const beforeActivity = Boolean(startDate && key < startDate);
      const afterActivity = Boolean(endDate && (!startDate || endDate >= startDate) && key > endDate);
      const outsideState = beforeActivity ? 'not-started' : afterActivity ? 'ended' : '';
      // nextReward是本次可领取额，已签到时为零；未来未发奖日期沿交接约定展示大奖。
      return { date: key, label: current ? '今日' : key.slice(5).replace('-', '.'), current,
        ...(pendingRepair ? { rewardState: 'PENDING_CLAIM' } : {}),
        reward: record ? money(record.rewardAmount) : pendingRepair ? money(pendingRepair.rewardAmount) : current ? blindBox ? '盲盒' : money(status.dailyReward) : !beforeActivity && !afterActivity && index > 4 ? '大奖' : null,
        state: current ? status.signedToday ? 'signed' : 'today' : index > 4 ? outsideState || 'future' : dates.has(key) || pendingRepair ? 'signed' : outsideState || 'unknown' };
    }),
    // A known single-cycle reward is informational. It does not establish an
    // independent claim endpoint, eligibility or a real reward identifier.
    milestones: Number.isInteger(status.streakRewardDays) && status.streakRewardDays > 0 && money(status.streakReward) !== '—'
      ? [{ id: 'single-cycle', days: status.streakRewardDays, amount: money(status.streakReward), canClaim: false }] : [],
    showPool: false,
    streakRule: week ? `本周期（${week.startDate} 至 ${week.endDate}）已签到 ${week.signedDays}/${cycleDays} 天；${week.cumulative ? '按累计签到次数取对应行，断签不清次数；' : '每日按对应日期取奖励；'}投注达到最高档发放，满签按各签到日最低档另加 ${money(week.fullReward)} 元预估。${awardWay === 2 ? '签到只冻结奖励，后台审核并成功派发后到账。' : awardWay === 3 ? '签到先冻结待领，活动结束前可一键领取，跨周不清零。' : '现金自动发至中心钱包。'}${status.autoRepair ? '当天签到请求先按日期补签达标漏签日，再签今天；只读查询不补签。' : ''}已签日期展示原冻结或已发金额，以独立奖励状态为准。` : Number.isInteger(status.streakRewardDays) && status.streakRewardDays > 0 && money(status.streakReward) !== '—'
      ? `每连续签到 ${status.streakRewardDays} 天，额外奖励 ${money(status.streakReward)} 元` : '',
    turnoverRule: rewardPartsLabel || (isTemplateSignIn(status, activity) ? '奖励按本活动配置发放，各项流水以奖励明细为准' : '') || (status.turnoverMultiplier != null && status.turnoverMultiplier !== '' && Number.isFinite(Number(status.turnoverMultiplier)) && Number(status.turnoverMultiplier) >= 0
      ? `奖励流水要求为奖励金额的 ${status.turnoverMultiplier} 倍` : '')
  };
}

export function createActivitySignIn({ api, getToken, notify = () => {}, onClaimed = () => {} }) {
  const state = reactive({ activity: null, model: null, signCategory: 0, records: [], page: 0, total: null, hasMore: false,
    loading: false, loadingMore: false, busy: '', error: '', recordsError: '', recordsRetryPage: 1, unresolved: false, loggedIn: false });
  let revision = 0, active = true, identity = '', request = null, lastStatus = null;
  const pendingWrites = new Map();
  const keyFor = activity => `${activity?.id || ''}:${getToken()}`;
  const current = (version, key) => active && revision === version && identity === key && keyFor(state.activity) === key;
  function reset(activity) {
    lastStatus = null;
    Object.assign(state, { activity, model: null, signCategory: 0, records: [], page: 0, total: null, hasMore: false, loading: false,
      loadingMore: false, busy: '', error: '', recordsError: '', recordsRetryPage: 1, unresolved: false, loggedIn: Boolean(getToken()) });
  }
  function unpack(result) {
    if (!Array.isArray(result?.data)) throw new Error('签到记录查询失败，请重试');
    return result.data.filter(row => row.id != null && validSignDate(row.signDate));
  }
  function pagination(result, page) {
    state.page = page;
    state.total = result.total !== '' && result.total != null && Number.isInteger(Number(result.total)) && Number(result.total) >= 0 ? Number(result.total) : null;
    state.hasMore = state.total == null ? result.data.length === 50 : page * 50 < state.total;
  }
  async function load(activity = state.activity) {
    const key = keyFor(activity);
    if (request?.key === key && active && identity === key) return request.promise;
    if (identity === key && state.busy) return;
    const version = ++revision;
    if (identity !== key) reset(activity);
    identity = key;
    if (pendingWrites.has(key)) state.busy = 'sign';
    state.activity = activity;
    state.loggedIn = Boolean(getToken());
    state.error = ''; state.recordsError = ''; state.loadingMore = false;
    state.recordsRetryPage = 1;
    if (!isSignInActivityType(activity?.activityType)) { state.error = '该活动不是可用的签到活动'; state.model = null; return; }
    if (!active || !state.loggedIn || !activity?.id) return;
    state.loading = true;
    const promise = (async () => {
      const readSnapshot = () => Promise.allSettled([
        Promise.resolve().then(() => state.signCategory ? api.status(activity.id, state.signCategory) : api.status(activity.id)),
        Promise.resolve().then(() => api.records({ activityId: activity.id, pageNumber: 1, pageSize: 50 }))
      ]);
      let [status, history] = await readSnapshot();
      if (!current(version, key)) return;
      if (status.status === 'fulfilled' && history.status === 'fulfilled' && manualRecordMismatch(status.value, history.value)) {
        [status, history] = await readSnapshot();
        if (!current(version, key)) return;
        if (status.status === 'fulfilled' && history.status === 'fulfilled' && manualRecordMismatch(status.value, history.value)) {
          state.records = [];
          history = { status: 'rejected', reason: new Error('签到记录与领取状态暂未同步，请刷新状态后核对') };
        }
      }
      if (history.status === 'fulfilled') {
        try { state.records = unpack(history.value); pagination(history.value, 1); }
        catch (error) { state.recordsError = error.message; }
      } else state.recordsError = history.reason?.message || '签到记录查询失败，请重试';
      if (status.status === 'fulfilled') {
        try { state.model = signInModel(activity, status.value, state.records); lastStatus = { ...status.value }; state.signCategory = state.model.signCategory; }
        catch (error) { state.error = error.message; }
      } else state.error = status.reason?.message || '签到状态查询失败，请重试';
      if (state.error || state.recordsError) {
        if (state.model) { state.model.canSign = false; state.model.canClaim = false; state.model.canClaimAll = false; }
      } else state.unresolved = false;
    })().finally(() => {
      if (current(version, key)) state.loading = false;
      if (request?.promise === promise) request = null;
    });
    request = { key, promise };
    return promise;
  }
  async function more() {
    if (!active || state.loading || state.busy || state.loadingMore || !state.hasMore) return;
    const version = revision, key = identity, page = state.page + 1;
    if (!current(version, key)) return;
    state.loadingMore = true; state.recordsError = ''; state.recordsRetryPage = page;
    try {
      const result = await api.records({ activityId: state.activity.id, pageNumber: page, pageSize: 50 });
      if (!current(version, key)) return;
      const rows = unpack(result), ids = new Set(state.records.map(row => String(row.id)));
      state.records.push(...rows.filter(row => !ids.has(String(row.id))));
      pagination(result, page);
      if (lastStatus && state.model) state.model = { ...signInModel(state.activity, lastStatus, state.records), canSign: state.model.canSign };
    } catch (error) { if (current(version, key)) state.recordsError = error.message || '签到记录查询失败，请重试'; }
    finally { if (current(version, key)) state.loadingMore = false; }
  }
  async function write(action) {
    const key = identity, version = revision;
    const allowed = action === 'CLAIM' ? state.model?.canClaim || state.model?.canClaimAll : state.model?.canSign;
    if (!current(version, key) || !state.loggedIn || state.loading || state.loadingMore || state.busy || pendingWrites.has(key) || state.unresolved || state.error || state.recordsError || !allowed) return;
    const activity = state.activity;
    const operation = {};
    pendingWrites.set(key, operation);
    state.busy = action === 'CLAIM' ? 'claim' : 'sign';
    const batchIds = action === 'CLAIM' && state.model?.canClaimAll ? [...state.model.pendingRecordIds] : [];
    let result, failure, ownsWrite = false;
    try { result = await api.sign(activity.id, action === 'CLAIM' ? batchIds.length ? { action, idList: batchIds } : { action, targetId: state.model.pendingRecordId } : { action, ...(state.signCategory ? { signCategory: state.signCategory } : {}) }); }
    catch (error) { failure = error; }
    finally {
      ownsWrite = pendingWrites.get(key) === operation;
      if (ownsWrite) { pendingWrites.delete(key); if (identity === key) state.busy = ''; }
    }
    if (!ownsWrite || !active || keyFor(state.activity) !== key || identity !== key) return;
    // Read back after every outcome. A timeout never automatically repeats SIGN.
    await load(activity);
    if (!active || identity !== key || keyFor(state.activity) !== key) return;
    if (batchIds.length) {
      // 领取可能包含跨周、超出首屏的凭证；逐页只读回查，不因超时自动重发领取。
      while (!state.error && !state.recordsError && state.hasMore && batchIds.some(id => !state.records.some(row => Number(row.id) === id))) {
        const before = state.records.length;
        await more();
        if (!active || identity !== key || keyFor(state.activity) !== key) return;
        if (state.records.length === before) break;
      }
      const credited = batchIds.every(id => state.records.some(row => Number(row.id) === id && paid(row))) && !state.error && !state.recordsError;
      if (credited) {
        state.unresolved = false;
        notify(result?.status === 'COMPLETED' && Number(result.rewardAmount) > 0 ? `签到奖励 ${result.rewardAmount} 元已发至中心钱包` : '所选签到奖励已到账，请查看奖励记录', 'success');
        onClaimed();
      } else {
        state.unresolved = Boolean(state.error || state.recordsError || result?.status === 'COMPLETED');
        notify(failure?.message || '累计领奖结果待核对，请刷新记录，不要重复提交');
      }
      return;
    }
    const record = state.records.find(row => row.signDate === state.model?.today && signedReceipt(row, state.activity?.activityType === 'WEEK_SIGN_IN'));
    if (action === 'SIGN' && state.model?.week && state.model.signedToday && record && !state.error && !state.recordsError) {
      if (state.model.awardWay === 2 && state.model.reviewPendingCount > 0) {
        state.unresolved = false; notify(`签到已登记，${state.model.reviewPendingCount} 笔奖励待后台审核或派发`, 'success'); return;
      }
      if (state.model.awardWay === 3 && state.model.canClaimAll) {
        state.unresolved = false; notify(`签到已登记，累计 ${state.model.pendingAmount} 元待领取`, 'success'); return;
      }
    }
    const signedPending = action === 'SIGN' && state.model?.manualClaim && state.model?.signedToday && state.model?.canClaim &&
      state.records.some(row => Number(row.id) === state.model.pendingRecordId && row.signDate === state.model.today && Number(row.status) === 1);
    if (signedPending && !state.error && !state.recordsError) {
      state.unresolved = false;
      if (revision === version + 1) notify('签到成功，请手动领取今日奖励', 'success');
      return;
    }
    if (action === 'CLAIM' && result?.status === 'PENDING' && state.model?.venueBonusIds.length && !state.error && !state.recordsError) {
      state.unresolved = false; notify('原签到奖励已保留，请核对场馆原单状态', 'success');
      if (Number(result.rewardAmount) > 0) onClaimed();
      return;
    }
    const confirmed = state.model?.signedToday && record && !state.error && !state.recordsError;
    if (confirmed) {
      if (revision === version + 1) {
        notify(state.model?.templateSignIn ? `今日签到奖励 ${record.rewardAmount} 元已到账，请查看奖励及钱包记录` : Number(record.rewardAmount) > 0 ? (result?.autoRepaired > 0 && Number(result.rewardAmount) > 0 ? `签到完成，已自动补签${result.autoRepaired}天，合计 ${result.rewardAmount} 元已发至中心钱包` : `今日已签到，${record.rewardAmount} 元奖励已发至中心钱包`) : '今日已签到，本次奖励为0元，未产生钱包账变', 'success');
        onClaimed();
      }
    } else {
      const acknowledged = result?.recordId && result.status === 'COMPLETED';
      state.unresolved = Boolean(state.error || state.recordsError || acknowledged || state.model?.signedToday);
      if (revision === version + 1) notify(failure?.message || '签到结果尚未确认，请刷新状态后查看记录');
    }
  }
  async function selectMode(category) {
    if (!active || state.loading || state.loadingMore || state.busy || state.unresolved || state.model?.signedToday || !state.model?.modes?.some(mode => mode.id === category) || category === state.signCategory) return;
    state.signCategory = category;
    await load(state.activity);
  }
  function sign() { return write('SIGN'); }
  function claim() { return write('CLAIM'); }
  function suspend() { active = false; ++revision; request = null; state.loading = false; state.loadingMore = false; }
  function resume() { active = true; }
  function dispose() { suspend(); identity = ''; pendingWrites.clear(); reset(null); }
  return { state, load, more, selectMode, sign, claim, suspend, resume, dispose };
}
